import type { ReactNode } from "react";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { Link } from "@mui/material";
import clsx from "clsx";

import appConfig from "config/appConfig";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

// import DiscordIcon from "assets/icons/filled/discord.svg?react";
// import FacebookIcon from "assets/icons/filled/facebook.svg?react";
// import GobletIcon from "assets/icons/filled/goblet.svg?react";
import CloseIcon from "assets/icons/filled/close-circle-2.svg?react";
import GramophoneIcon from "assets/icons/filled/gramophone.svg?react";
import HomeIcon from "assets/icons/filled/home.svg?react";
import InfoIcon from "assets/icons/filled/info.svg?react";
import LetterIcon from "assets/icons/filled/letter.svg?react";
import LogoutIcon from "assets/icons/filled/logout.svg?react";
import ClosedMenuIcon from "assets/icons/filled/menu-close.svg?react";
import OpenedMenuIcon from "assets/icons/filled/menu-open.svg?react";

// import RedditIcon from "assets/icons/filled/reddit.svg?react";
import RugbyBallIcon from "assets/icons/filled/rugby-ball.svg?react";
import TelegramIcon from "assets/icons/filled/telegram.svg?react";
import TwoCardsIcon from "assets/icons/filled/two-cards.svg?react";
import UserIcon from "assets/icons/filled/user-plus.svg?react";
import TwitterIcon from "assets/icons/filled/x.svg?react";
import LogoIcon from "assets/icons/illustrations/logo.svg?react";
import MagnifierIcon from "assets/icons/outlined/magnifier.svg?react";
import DiceIconUrl from "assets/images/dice.webp";

import { getPaths } from "core/getPaths";
import { useAuth, useUser } from "shared/hooks";

import useResize from "shared/hooks/useResize";

// import { NavItem, NavItemText, SignUpButton, SocialItem, StyledNavMenu } from "./style";
import ChipsTokenWidget from "./ChipsTokenWidget";

import styles from "./NavMenu.module.scss";

import LanguageDropdown from "../LanguageDropdown";

const minSwipeDistance = 50;

const { member: memberRoutes, games: gamesRoutes } = getPaths();

export interface NavMenuMethodsHandlers {
	toggleNavMenu: () => void;
}

interface NavMenuProps {
	onOpenAuthModal: (flow: "bonus" | "login" | "signUp") => void;
	onOpenGameSearchModal: () => void;
}

const NavMenu = forwardRef<NavMenuMethodsHandlers, NavMenuProps>(({ onOpenAuthModal, onOpenGameSearchModal }, ref) => {
	const { t } = useTranslation();

	const { getData: getUserData } = useUser();
	const { isAuthorized } = getUserData();

	const { logout } = useAuth();

	const { isMobile } = useResize();

	const [opened, setOpened] = useState(!isMobile);

	const touchStart = useRef(0);
	const touchEnd = useRef(0);

	useEffect(() => {
		setOpened(!isMobile);
	}, [isMobile]);

	const menuList = useMemo(
		() =>
			[
				{
					label: t("homepage"),
					icon: <HomeIcon />,
					link: memberRoutes.home.getPath()
				},
				isMobile
					? {
							label: t("search"),
							icon: <MagnifierIcon className="svg-paths:fill-yellow-300" />,
							onClick: () => onOpenGameSearchModal()
						}
					: undefined!,
				{
					label: t("all_games"),
					icon: <TwoCardsIcon />,
					link: gamesRoutes.all.getPath()
				},
				{
					label: t("sports"),
					icon: <RugbyBallIcon />,
					onClick: () => {
						window.open(appConfig.GLOBAL_CONSTANTS.DIGITAIN_SPORTS_URL, "_blank");
					}
				},
				{
					label: t("promotions"),
					icon: <GramophoneIcon />,
					link: memberRoutes.promotions.getPath()
				}
				// {
				// 	label: t("tournaments"),
				// 	icon: <GobletIcon />,
				// 	link: memberRoutes.tournaments.getPath()
				// }
			].filter(Boolean),
		[t, isMobile, onOpenGameSearchModal]
	);

	const submenuList = useMemo(() => {
		const items: {
			label: string;
			icon: ReactNode;
			link?: string;
			onClick?: () => void;
		}[] = [
			{
				label: t("contact_us"),
				icon: <LetterIcon />,
				link: memberRoutes.contactUs.getPath()
			},
			{
				label: t("info"),
				icon: <InfoIcon />,
				link: memberRoutes.info.getPath()
			}
		];

		if (isAuthorized) {
			items.push({
				label: t("logout"),
				icon: <LogoutIcon />,
				onClick: () => logout()
			});
		}

		return items;
	}, [logout, isAuthorized, t]);

	const socialList = useMemo(
		() => [
			{
				icon: <TwitterIcon />,
				link: appConfig.GLOBAL_CONSTANTS.SOCIAL_X_URL
			},
			// {
			// 	icon: <FacebookIcon />,
			// 	link: "2"
			// },
			{
				icon: <TelegramIcon />,
				link: appConfig.GLOBAL_CONSTANTS.SOCIAL_TELEGRAM_URL
			}
			// {
			// 	icon: <RedditIcon />,
			// 	link: "4"
			// },
			// {
			// 	icon: <DiscordIcon />,
			// 	link: "5"
			// }
		],
		[]
	);

	const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		touchEnd.current = 0;
		touchStart.current = e.targetTouches[0]?.clientX ?? 0;
	};

	const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		touchEnd.current = e.targetTouches[0]?.clientX ?? 0;
	};

	const onTouchEnd = () => {
		if (!touchStart.current || !touchEnd.current) return;
		const distance = touchStart.current - touchEnd.current;
		const isLeftSwipe = distance > minSwipeDistance;
		const isRightSwipe = distance < -minSwipeDistance;
		if (isLeftSwipe) {
			setOpened(false);
		} else if (isRightSwipe) {
			setOpened(true);
		}
	};

	const toggleMenu = () => {
		setOpened(!opened);
	};

	const closeMenuOnMobile = () => {
		if (isMobile) {
			setOpened(false);
		}
	};

	const onOpenLanguageDropdown = () => {
		if (!opened) {
			toggleMenu();
		}
	};

	useImperativeHandle(ref, () => ({
		toggleNavMenu: toggleMenu
	}));

	return (
		<div
			className={clsx(styles.menu, opened && styles.opened)}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
		>
			<div className="w-full flex-col flex-1">
				<div className={clsx("flex items-center px-3 pt-4", opened ? "justify-between" : "justify-center")}>
					<Link
						to={memberRoutes.home.getPath()}
						component={RouterLink}
						className="no-underline w-full cursor-pointer"
						onClick={closeMenuOnMobile}
					>
						<div
							className={clsx(
								"flex items-center gap-[3px] transition-all overflow-hidden opacity-100",
								!opened && "max-w-0 opacity-0"
							)}
						>
							<LogoIcon className={"w-[115px] mt-0.5"} />
							<img src={DiceIconUrl} alt="Dice" className="w-5" />
						</div>
					</Link>
					<div className="relative w-6 h-6 cursor-pointer" onClick={toggleMenu}>
						{opened ? isMobile ? <CloseIcon className="w-6 h-6" /> : <ClosedMenuIcon /> : <OpenedMenuIcon />}
					</div>
				</div>
				<div className="mt-4 px-3">
					<ChipsTokenWidget collapsed={!opened} />
				</div>
				{!isAuthorized && (
					<div className={clsx("mt-4", opened ? "px-3" : "flex justify-center")}>
						<button
							className={clsx(styles.signUpBtn, !opened && "w-6 h-6 rounded-full p-[3px] gap-0")}
							onClick={() => {
								closeMenuOnMobile();
								onOpenAuthModal("bonus");
							}}
						>
							<UserIcon />
							<p className={clsx("font-mic text-base text-[#5C2405]", !opened && "max-w-0 overflow-hidden opacity-0")}>
								{t("sign_up")}
							</p>
						</button>
					</div>
				)}
				<div className={clsx("w-full mt-2 flex flex-col", !opened && "items-center")}>
					{menuList.map(item => (
						<Link
							key={`nav-link-${item.label}`}
							to={item.link}
							component={RouterLink}
							className="no-underline w-full cursor-pointer"
							onClick={() => {
								if (item.onClick) item.onClick();
								closeMenuOnMobile();
							}}
						>
							<div className={clsx(styles.navItem, "group", opened ? styles.opened : "w-full justify-center")}>
								<div className="relative w-6 h-6 flex items-center justify-center">{item.icon}</div>
								<p className={clsx(styles.navItemText, !opened && styles.opened)}>{item.label}</p>
							</div>
						</Link>
					))}
				</div>
				<div className="w-full px-2 mt-[7px]">
					<hr className="border-0 w-full h-0.5 bg-black opacity-40" />
				</div>
				<div className={clsx("w-full mt-2 flex flex-col", !opened && "items-center")}>
					{submenuList.map(item => {
						const content = (
							<div
								key={item.label}
								className={clsx(styles.navItem, "group", opened ? styles.opened : "w-full justify-center")}
								onClick={() => {
									if (item.onClick) item.onClick();
									closeMenuOnMobile();
								}}
							>
								<div className="relative w-6 h-6 flex items-center justify-center">{item.icon}</div>
								<p className={clsx(styles.navItemText, !opened && styles.opened)}>{item.label}</p>
							</div>
						);
						if (item?.link) {
							return (
								<Link
									key={`nav-link-${item.label}`}
									to={item.link}
									component={RouterLink}
									className="no-underline w-full cursor-pointer"
									onClick={closeMenuOnMobile}
								>
									{content}
								</Link>
							);
						}

						return content;
					})}
				</div>
				<div className={clsx("mt-[7px]", opened ? "px-3" : "flex justify-center")}>
					<LanguageDropdown collapsed={!opened} onOpenDropdown={onOpenLanguageDropdown} />
				</div>
			</div>
			<div className={clsx("flex justify-between items-center px-3 py-4", !opened && "flex-col gap-2")}>
				{socialList.map(item => (
					<Link key={item.link} to={item.link} component={RouterLink} className="no-underline" target="_blank">
						<div className={styles.socialItem}>{item.icon}</div>
					</Link>
				))}
			</div>
		</div>
	);
});

export default NavMenu;
